import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Slider
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { format } from 'date-fns';
import { getRecordingUrl } from '../services/api';

function AudioPlayer({ recording, onClose }) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const loadAudioUrl = useCallback(async () => {
    try {
      // Get presigned URL for the recording blob path
      const data = await getRecordingUrl(recording.blobPath, 'play');
      setAudioUrl(data.url);
    } catch (err) {
      console.error('Failed to load audio:', err);
    } finally {
      setLoading(false);
    }
  }, [recording]);

  useEffect(() => {
    loadAudioUrl();
  }, [loadAudioUrl]);

  // Auto-play when audio loads
  const handleCanPlay = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (event, newValue) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = newValue;
    setCurrentTime(newValue);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Recording Details
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Extension:</strong> {recording.extensionName || 'Unknown'} ({recording.extension})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Phone:</strong> {recording.otherPartyNumber || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Date:</strong> {recording.callDateTime ? format(new Date(recording.callDateTime), 'MMM dd, yyyy HH:mm:ss') : 'Date Unknown'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Direction:</strong> {recording.direction}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ py: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Loading audio...
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <IconButton onClick={togglePlayPause} color="primary" size="large">
                {playing ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 100 }}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </Typography>
            </Box>
            
            <Box sx={{ px: 2 }}>
              <Slider
                value={currentTime}
                max={duration}
                onChange={handleSeek}
                aria-label="Audio progress"
                sx={{ width: '100%' }}
              />
            </Box>

            <audio
              ref={audioRef}
              src={audioUrl}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onCanPlay={handleCanPlay}
              onEnded={() => setPlaying(false)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AudioPlayer;